import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { createOrder, createStripeCheckout, formatRON, imgUrl, validateVoucher } from "@/lib/api";
import { ext } from "@/lib/ext";

const Field = ({ label, name, value, onChange, type = "text", required = true, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}{required && " *"}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`checkout-${name}`}
      className="w-full border border-border bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
    />
  </label>
);

const PHONE_PREFIXES = ["+40", "+44", "+49", "+33", "+39", "+34", "+31", "+32", "+43", "+41", "+1", "+353", "+30", "+351", "+48", "+36", "+359", "+380"];

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { t, lang } = useLang();
  const content = useContent();
  const shippingCountries = content?.shipping_countries || [];
  const e = ext[lang];
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null); // {code, percent}
  const [payMethod, setPayMethod] = useState("card"); // 'card' | 'cod'
  const [form, setForm] = useState({
    name: "", email: "", phonePrefix: "+40", phoneNumber: "", address: "", city: "", county: "", postal: "", country: "România", notes: "",
  });
  // Build country list from admin table; ensure Romania exists (no hardcoded free label)
  const hasRO = shippingCountries.some((c) => /^rom[aâ]nia$/i.test((c.name_ro || "").trim()) || /^romania$/i.test((c.name_en || "").trim()));
  const countryOptions = hasRO ? shippingCountries : [{ name_ro: "România", name_en: "Romania", price: 0 }, ...shippingCountries];
  const discount = voucher ? Math.round(subtotal * voucher.percent / 100) : 0;
  const selectedCountry = countryOptions.find((c) => c.name_ro === form.country || c.name_en === form.country);
  const baseShipping = selectedCountry ? (parseInt(selectedCountry.price, 10) || 0) : 0;
  // Admin-toggleable free shipping for Romania above a threshold (backend is authoritative)
  const fsr = content?.free_shipping_ro || {};
  const isRO = selectedCountry ? (/^rom[aâ]nia$/i.test((selectedCountry.name_ro || "").trim()) || /^romania$/i.test((selectedCountry.name_en || "").trim())) : true;
  const freeShip = !!(fsr.enabled && isRO && (subtotal - discount) >= (parseInt(fsr.threshold, 10) || 0));
  const shipping = freeShip ? 0 : baseShipping;
  const total = Math.max(0, subtotal - discount) + shipping;

  const applyVoucher = async () => {
    if (!voucherInput.trim()) return;
    try { const v = await validateVoucher(voucherInput.trim()); setVoucher(v); toast.success(`${e.voucherOk} · -${v.percent}%`); }
    catch { setVoucher(null); toast.error(e.voucherBad); }
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error(t.checkout.empty); return; }
    const digits = (form.phoneNumber || "").replace(/\D/g, "");
    if (digits.length < 6) { toast.error(lang === "ro" ? "Introdu un număr de telefon valid" : "Enter a valid phone number"); return; }
    setSubmitting(true);
    try {
      const customer = {
        name: form.name, email: form.email, phone: `${form.phonePrefix} ${form.phoneNumber}`.trim(),
        address: form.address, city: form.city, county: form.county, postal: form.postal,
        country: form.country, notes: form.notes,
      };
      const orderItems = items.map((i) => ({
        slug: i.slug, name: i.name, size: i.size, color: i.color, qty: i.qty, price: i.price, image: i.image,
      }));
      if (payMethod === "card") {
        const { checkout_url } = await createStripeCheckout({
          items: orderItems, customer, voucher: voucher ? voucher.code : undefined,
          origin_url: window.location.origin,
        });
        window.location.href = checkout_url; // redirect to Stripe (card / Apple Pay / Google Pay)
        return;
      }
      const order = await createOrder({
        items: orderItems,
        customer,
        voucher: voucher ? voucher.code : undefined,
      });
      clear();
      navigate(`/order/${order.id}`);
    } catch (err) {
      toast.error("Error placing order");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 pt-28" data-testid="checkout-empty">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t.checkout.empty}</p>
        <Link to="/shop" className="border border-border px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">
          {t.cart.continue}
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-28" data-testid="checkout-page">
      <div className="border-b border-border p-5 sm:p-8">
        <h1 className="text-2xl uc font-semibold sm:text-3xl">{t.checkout.title}</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <form onSubmit={submit} className="space-y-6 border-b border-border p-6 sm:p-10 lg:border-b-0 lg:border-r">
          <div>
            <h2 className="mb-4 font-mono text-xs uppercase tracking-widest text-accent">{t.checkout.contact}</h2>
            <div className="space-y-4">
              <Field label={t.checkout.name} name="name" value={form.name} onChange={onChange} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t.checkout.email} name="email" type="email" value={form.email} onChange={onChange} />
                <label className="block">
                  <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.checkout.phone} *</span>
                  <div className="flex">
                    <select name="phonePrefix" value={form.phonePrefix} onChange={onChange} data-testid="checkout-phone-prefix"
                      className="border border-r-0 border-border bg-background px-2 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent">
                      {PHONE_PREFIXES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <input type="tel" name="phoneNumber" value={form.phoneNumber} onChange={onChange} required data-testid="checkout-phone"
                      inputMode="tel" placeholder="7xx xxx xxx"
                      className="w-full border border-border bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
                  </div>
                </label>
              </div>
            </div>
          </div>
          <div>
            <h2 className="mb-4 font-mono text-xs uppercase tracking-widest text-accent">{t.checkout.shipping}</h2>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "ro" ? "Țară" : "Country"} *</span>
                <select
                  name="country" value={form.country} onChange={onChange} data-testid="checkout-country"
                  className="w-full border border-border bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {countryOptions.map((c, i) => {
                    const price = parseInt(c.price, 10) || 0;
                    const nm = (lang === "ro" ? c.name_ro : c.name_en) || c.name_ro || c.name_en;
                    const label = price > 0 ? `${nm} — ${formatRON(price)}` : nm;
                    return <option key={i} value={c.name_ro || c.name_en}>{label}</option>;
                  })}
                </select>
              </label>
              <Field label={t.checkout.address} name="address" value={form.address} onChange={onChange} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label={t.checkout.city} name="city" value={form.city} onChange={onChange} />
                <Field label={t.checkout.county} name="county" value={form.county} onChange={onChange} />
                <Field label={t.checkout.postal} name="postal" value={form.postal} onChange={onChange} />
              </div>
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.checkout.notes}</span>
                <textarea
                  name="notes" value={form.notes} onChange={onChange} rows={3} data-testid="checkout-notes"
                  className="w-full border border-border bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </label>
            </div>
          </div>
          <div>
            <h2 className="mb-4 font-mono text-xs uppercase tracking-widest text-accent">{lang === "ro" ? "Plată" : "Payment"}</h2>
            <div className="space-y-2">
              <label className={`flex cursor-pointer items-center gap-3 border p-4 transition-colors ${payMethod === "card" ? "border-foreground" : "border-border"}`} data-testid="pay-method-card">
                <input type="radio" name="payMethod" checked={payMethod === "card"} onChange={() => setPayMethod("card")} />
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest">{lang === "ro" ? "Card · Apple Pay · Google Pay" : "Card · Apple Pay · Google Pay"}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "ro" ? "Plată securizată online (Stripe)" : "Secure online payment (Stripe)"}</div>
                </div>
              </label>
              <label className={`flex cursor-pointer items-center gap-3 border p-4 transition-colors ${payMethod === "cod" ? "border-foreground" : "border-border"}`} data-testid="pay-method-cod">
                <input type="radio" name="payMethod" checked={payMethod === "cod"} onChange={() => setPayMethod("cod")} />
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest">{lang === "ro" ? "Ramburs (plata la livrare)" : "Cash on delivery"}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "ro" ? "Plătești curierului la primire" : "Pay the courier on arrival"}</div>
                </div>
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="place-order"
            className="w-full bg-foreground py-5 text-base font-bold uppercase tracking-[0.2em] text-background transition-colors hover:bg-accent disabled:opacity-50"
          >
            {submitting ? t.checkout.placing : (payMethod === "card" ? (lang === "ro" ? "Plătește " + formatRON(total) : "Pay " + formatRON(total)) : t.checkout.place)}
          </button>
        </form>

        <div className="p-6 sm:p-10">
          <h2 className="mb-6 font-mono text-xs uppercase tracking-widest text-accent">{t.checkout.summary}</h2>
          <div className="space-y-4">
            {items.map((i) => (
              <div key={`${i.slug}-${i.size}-${i.color}`} className="flex gap-4 border-b border-border pb-4">
                <img src={imgUrl(i.image)} alt={i.name} className="h-24 w-20 object-cover" />
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex justify-between gap-2">
                    <span className="font-bold uppercase tracking-tight">{i.name}</span>
                    <span className="font-mono text-sm">{formatRON(i.price * i.qty)}</span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {i.size} · {i.color} · x{i.qty}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex border border-border">
            <input value={voucherInput} onChange={(ev) => setVoucherInput(ev.target.value)} placeholder={e.voucher} data-testid="voucher-input"
              className="w-full bg-transparent px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest focus:outline-none" />
            <button type="button" onClick={applyVoucher} data-testid="voucher-apply" className="bg-foreground px-4 font-mono text-[11px] uppercase tracking-widest text-white">{e.voucherApply}</button>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground">
              <span>{t.cart.subtotal}</span><span>{formatRON(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between font-mono text-xs uppercase tracking-widest" data-testid="checkout-discount">
                <span>{e.discount} ({voucher.code})</span><span>-{formatRON(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="checkout-shipping-line">
              <span>{t.cart.shipping}{form.country ? ` · ${lang === "ro" ? (selectedCountry?.name_ro || form.country) : (selectedCountry?.name_en || form.country)}` : ""}</span>
              <span>{freeShip ? (lang === "ro" ? "GRATUITĂ" : "FREE") : (shipping > 0 ? formatRON(shipping) : "—")}</span>
            </div>
            <div className="flex items-end justify-between border-t border-border pt-3">
              <span className="font-mono text-xs uppercase tracking-widest">{t.cart.total}</span>
              <span className="text-3xl font-black" data-testid="checkout-total">{formatRON(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
